/**
 * 判断URL是否包含OSS视频截图参数
 * @param url 图像URL
 * @returns 是否包含视频截图参数
 */
export const isOssVideoSnapshot = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  
  // 检查是否包含x-oss-process参数
  if (!url.includes('x-oss-process=')) {
    return false;
  }
  
  // 检查是否包含video/snapshot参数
  if (!url.includes('video/snapshot')) {
    return false;
  }
  
  // 更精确的匹配，确保参数格式正确
  const ossProcessRegex = /x-oss-process=video\/snapshot(?:,\w+=[^&,]*)*(?:,t_\d+)?(?:,ar_auto)?(?:,f_jpg)?/;
  return ossProcessRegex.test(url);
};

/**
 * 检查URL是否已经包含宽度参数
 * @param url 图像URL
 * @returns 是否已包含宽度参数
 */
export const hasWidthParameter = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  
  // 检查是否包含宽度相关参数
  const widthParamRegex = /[,&]w_(?:auto|\d+)/;
  return widthParamRegex.test(url);
};

/**
 * 为OSS视频截图URL添加宽度参数
 * @param url 原始URL
 * @param width 宽度值，可以是数字或'auto'
 * @param limitScale 是否限制只能缩小不能放大
 * @returns 处理后的URL
 */
export const addWidthParameter = (
  url: string, 
  width: number | 'auto' = 'auto', 
  limitScale: boolean = true
): string => {
  if (!url || typeof url !== 'string') {
    return url;
  }
  
  // 如果不是OSS视频截图URL，直接返回
  if (!isOssVideoSnapshot(url)) {
    return url;
  }
  
  // 如果已经包含宽度参数，直接返回
  if (hasWidthParameter(url)) {
    return url;
  }
  
  // 构建宽度参数
  let widthParam = `,w_${width}`;
  if (limitScale && width !== 'auto') {
    widthParam += ',limit_1';
  }
  
  return url + widthParam;
};

/**
 * 智能处理图像URL，根据容器宽度动态添加宽度参数
 * @param url 原始URL
 * @param containerWidth 容器宽度（可选）
 * @returns 处理后的URL
 */
export const processImageUrl = (url: string, containerWidth?: number): string => {
  if (!isOssVideoSnapshot(url)) {
    return url;
  }
  
  // 如果已经有宽度参数，不重复添加
  if (hasWidthParameter(url)) {
    return url;
  }
  
  // 根据容器宽度决定参数
  if (containerWidth) {
    // 为了适配高分辨率屏幕，使用2倍宽度
    const targetWidth = Math.ceil(containerWidth * window.devicePixelRatio);
    return addWidthParameter(url, targetWidth, true);
  } else {
    // 没有指定容器宽度时，使用自动宽度
    return addWidthParameter(url, 'auto', true);
  }
};